import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { Help24, Overflow } from 'ui/icons';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { MenuItemOption } from 'ui/menu/types';
import { Tooltip } from 'ui/tooltip';
import { kebabCase } from 'utils/stringHelpers';
import {
  MenuWrapper,
  MetricBreakdownRow,
  StyledBreakdownCardHeader,
  StyledCampaignAnalyticsCard,
  StyledDivider,
  StyledLink,
} from './Styled';

type LinkContent = {
  search: string;
  href: string; // link location
};

type TitleProps = {
  title: string;
  titleHelperContent?: string;
};

type Props = {
  title: string;
  titleHelperContent?: string;
  linkout?: LinkContent;
  isAngularLink?: boolean;
  menuOptions?: MenuItemOption[];
  handleSelectionChange?: (filterId: string) => void;
  children: ReactNode | undefined;
};

const StyledTooltip = styled(Tooltip)`
  display: flex;
  align-self: center;
`;

const CardTitle = ({ title, titleHelperContent }: TitleProps) => {
  if (!titleHelperContent) {
    return <>{title}</>;
  }

  return (
    <FlexSpacer gap="half" alignItems="center">
      <span>{title}</span>
      <StyledTooltip tooltipContent={titleHelperContent}>
        <Help24 />
      </StyledTooltip>
    </FlexSpacer>
  );
};

export const MetricBreakdownCard = ({
  title,
  titleHelperContent,
  linkout,
  isAngularLink,
  menuOptions,
  handleSelectionChange,
  children,
  ...props
}: Props) => {
  return (
    <StyledCampaignAnalyticsCard {...props}>
      <StyledBreakdownCardHeader data-test={`card-header-${kebabCase(title)}`}>
        <CardTitle title={title} titleHelperContent={titleHelperContent} />
        <div>
          {linkout && (
            <StyledLink
              data-test="metric-breakdown-linkout"
              as={isAngularLink ? 'a' : Link}
              // @ts-ignore
              to={isAngularLink ? undefined : { pathname: linkout.href, search: linkout.search }}
              href={isAngularLink ? linkout.href : undefined}
            >
              View details
            </StyledLink>
          )}
          {menuOptions && (
            <MenuWrapper>
              <Menu
                handleSelectionChange={handleSelectionChange}
                horizontalAlignment={ALIGNMENT.RIGHT}
                options={menuOptions}
              >
                <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
                  <Overflow />
                </IconButton>
              </Menu>
            </MenuWrapper>
          )}
        </div>
      </StyledBreakdownCardHeader>
      <StyledDivider />
      <MetricBreakdownRow>{children}</MetricBreakdownRow>
    </StyledCampaignAnalyticsCard>
  );
};
